/**
 * A seção Plugins oficiais da administração (docs/spec/web-plugins.md, "Plugins oficiais"): o
 * catálogo que qualquer mestre instala pela aba Oficiais de "Instalar plugin…". Cada linha diz a
 * versão publicada, o que o plugin traz, para que sistemas serve, quantas mesas o usam (e quantas
 * estão numa versão antiga) e quando a versão saiu. "Adicionar plugin…" fica no cabeçalho; o "⋯" (ou
 * o botão direito, ou segurar o dedo) envia uma versão nova, muda os sistemas, tira e põe no catálogo
 * e apaga, este só sem mesas. Só os admins da configuração do servidor mudam; os outros veem.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api.js';
import { useLanguage, useT } from '../i18n/index.js';
import { PluginIcon, pluginKinds } from '../plugins/web/PluginParts.js';
import { localize } from '../plugins/web/sdk.js';
import type { AdminOfficialPlugin, KnownSystem } from '../shared.js';
import { describeError, useApp } from '../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { Button, Spinner } from '../ui/controls.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Icon } from '../ui/icons.js';
import { askConfirm } from '../ui/prompt.js';
import { toast } from '../ui/Toaster.js';
import { useLongPress } from '../ui/touch.js';
import { AdminPluginDialog, AdminPluginSystemsDialog } from './AdminPluginDialog.js';
import { useDateText } from './AdminUsers.js';

type Catalog = { plugins: AdminOfficialPlugin[]; canEdit: boolean; knownSystems: KnownSystem[] };
type OpenDialog = { kind: 'publish'; target: AdminOfficialPlugin | null } | { kind: 'systems'; target: AdminOfficialPlugin };

const COLUMNS = ['plugin', 'version', 'contents', 'systems', 'usage', 'updated'] as const;

export function AdminPlugins({ version, onChanged, actionsSlot }: { version: number; onChanged: () => void; actionsSlot: HTMLElement | null }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const token = useApp((s) => s.token);
  const date = useDateText();
  const press = useLongPress();
  const [data, setData] = useState<Catalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; plugin: AdminOfficialPlugin } | null>(null);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.adminPlugins(token).then(
      (result) => {
        if (!alive) return;
        setData(result);
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, version]);

  const nameOf = (plugin: AdminOfficialPlugin) => localize(plugin.name, language);

  const setHidden = async (plugin: AdminOfficialPlugin, hidden: boolean) => {
    if (!token) return;
    try {
      await api.adminPatchPlugin(token, plugin.id, { hidden });
      toast(t(hidden ? 'admin.plugins.hiddenDone' : 'admin.plugins.shownDone', { name: nameOf(plugin) }), 'success');
      onChanged();
    } catch (failure) {
      toast(describeError(failure), 'error');
    }
  };

  const remove = async (plugin: AdminOfficialPlugin) => {
    if (!token) return;
    const sure = await askConfirm({ title: t('admin.plugins.deleteTitle', { name: nameOf(plugin) }), message: t('admin.plugins.deleteText'), action: t('admin.plugins.delete'), danger: true });
    if (!sure) return;
    try {
      await api.adminDeletePlugin(token, plugin.id);
      toast(t('admin.plugins.deleteDone', { name: nameOf(plugin) }), 'success');
      onChanged();
    } catch (failure) {
      toast(describeError(failure), 'error');
    }
  };

  const entries = (plugin: AdminOfficialPlugin): MenuEntry[] => [
    { kind: 'title', label: nameOf(plugin) },
    { label: t('admin.plugins.newVersion'), onSelect: () => setDialog({ kind: 'publish', target: plugin }) },
    { label: t('admin.plugins.editSystems'), onSelect: () => setDialog({ kind: 'systems', target: plugin }) },
    plugin.hidden
      ? { label: t('admin.plugins.show'), onSelect: () => void setHidden(plugin, false) }
      : { label: t('admin.plugins.hide'), hint: t('admin.plugins.hideHint'), onSelect: () => void setHidden(plugin, true) },
    { kind: 'separator' },
    {
      label: t('admin.plugins.delete'),
      danger: true,
      disabled: plugin.rooms > 0,
      ...(plugin.rooms > 0 ? { hint: t('admin.plugins.deleteInUse', { n: plugin.rooms }) } : {}),
      onSelect: () => void remove(plugin),
    },
  ];

  const addButton = (
    <Button variant="action" icon="plus" onClick={() => setDialog({ kind: 'publish', target: null })}>
      {t('admin.plugins.add')}
    </Button>
  );

  return (
    <section className="rd-adminPlugins" aria-label={t('admin.section.plugins')}>
      {/* a ação da seção vai no cabeçalho da página, à direita do título (DESIGN.md, "Cabeçalho de página") */}
      {actionsSlot && data?.canEdit ? createPortal(addButton, actionsSlot) : null}
      <p className="rd-adminPlans__hint">{t(data && !data.canEdit ? 'admin.plugins.readOnly' : 'admin.plugins.hint')}</p>
      {error ? (
        <p className="rd-adminUsers__error" role="alert">
          {error}
        </p>
      ) : null}
      {!data && !error ? (
        <div className="rd-placeholder">
          <Spinner size={28} />
        </div>
      ) : null}
      {data && !data.plugins.length ? (
        <EmptyState icon="sheet" title={t('admin.plugins.none')} text={t('admin.plugins.noneText')}>
          {data.canEdit ? addButton : null}
        </EmptyState>
      ) : null}
      {data?.plugins.length ? (
        <>
          <p className="rd-adminUsers__count">{t('admin.plugins.count', { n: data.plugins.length })}</p>
          <div className="rd-adminTable rd-adminTable--plugins" role="table" aria-label={t('admin.section.plugins')}>
            <div className="rd-adminTable__row rd-adminTable__row--head" role="row">
              {COLUMNS.map((col) => (
                <span key={col} role="columnheader" className={`rd-adminTable__${col}`}>
                  {t(`admin.plugins.col.${col}`)}
                </span>
              ))}
              <span role="columnheader" />
            </div>
            {data.plugins.map((plugin) => {
              const name = nameOf(plugin);
              return (
                <div key={plugin.id} className={`rd-adminTable__row${plugin.hidden ? ' is-hidden' : ''}`} role="row" aria-label={name} {...(data.canEdit ? press.bind((anchor) => setMenu({ anchor, plugin })) : {})}>
                  <span role="cell" className="rd-adminTable__plugin">
                    <PluginIcon url={plugin.iconUrl} table={!plugin.sheets.length} size={32} />
                    <span className="rd-adminTable__pluginText">
                      <span className="rd-adminTable__pluginName">
                        <span className="rd-adminTable__name">{name}</span>
                        {plugin.hidden ? <span className="rd-pluginTag">{t('admin.plugins.hiddenTag')}</span> : null}
                      </span>
                      <span className="rd-adminTable__pluginId">{plugin.moduleId}</span>
                    </span>
                  </span>
                  <span role="cell" className="rd-adminTable__version" data-label={t('admin.plugins.col.version')}>
                    {plugin.version}
                  </span>
                  <span role="cell" className="rd-adminTable__contents">
                    {pluginKinds(t, plugin.sheets.length, plugin.tables.length)}
                  </span>
                  <span role="cell" className="rd-adminTable__systems" data-label={t('admin.plugins.col.systems')}>
                    {plugin.systems.length ? plugin.systems.map((system) => <span key={system} className="rd-pluginTag">{system}</span>) : <span className="rd-adminTable__none">{t('admin.plugins.noSystems')}</span>}
                  </span>
                  <span role="cell" className="rd-adminTable__usage" data-label={t('admin.plugins.col.usage')}>
                    {plugin.outdated ? t('admin.plugins.roomsOutdated', { n: plugin.rooms, old: plugin.outdated }) : plugin.rooms}
                  </span>
                  <span role="cell" className="rd-adminTable__updated" title={plugin.updatedByName ? t('admin.plugins.updatedBy', { name: plugin.updatedByName }) : undefined}>
                    {date(plugin.updatedAt)}
                  </span>
                  <span role="cell" className="rd-adminTable__actions">
                    {data.canEdit ? (
                      <button
                        type="button"
                        className="rd-iconBtn rd-adminTable__more"
                        aria-label={t('admin.plugins.more', { name })}
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          setMenu({ anchor: { x: r.right, y: r.bottom, end: true }, plugin });
                        }}
                      >
                        <Icon name="more" size={18} />
                      </button>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      ) : null}
      {menu ? <ContextMenu anchor={menu.anchor} label={nameOf(menu.plugin)} entries={entries(menu.plugin)} onClose={() => setMenu(null)} /> : null}
      {dialog?.kind === 'publish' && data ? (
        <AdminPluginDialog
          target={dialog.target}
          plugins={data.plugins}
          knownSystems={data.knownSystems}
          onDone={(changed) => {
            setDialog(null);
            if (changed) onChanged();
          }}
        />
      ) : null}
      {dialog?.kind === 'systems' && data ? (
        <AdminPluginSystemsDialog
          target={dialog.target}
          knownSystems={data.knownSystems}
          onDone={(changed) => {
            setDialog(null);
            if (changed) onChanged();
          }}
        />
      ) : null}
    </section>
  );
}
